import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import useStore from '@hooks/useStore';
import { observer } from 'mobx-react';
import { ReactComponent as ExclamationIcon } from '@assets/svg/exclamation.svg';

interface IConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    dialog: {
      '& .MuiDialog-paper': {
        width: '417px',
        background: '#FFFFFF 0% 0% no-repeat padding-box',
        borderRadius: 24,
        opacity: 1,
        padding: '24px',
      },
    },
    dialogTitle: {
      padding: 0,
      marginBottom: 0,
    },
    title: {
      textAlign: 'center',
      font: 'normal normal normal 22px SuraSans-Negrita',
      letterSpacing: 0,
      color: '#0033A0',
      opacity: 1,
      marginBottom: 12,
    },
    subtitle: {
      fontFamily: 'SuraSans-Regular',
      fontSize: '16px',
      color: '#0133A0',
      textAlign: 'left',
      marginTop: 16,
    },
    content: {
      padding: 0,
      marginBottom: 0,
    },
    policyContainer: {
      backgroundColor: '#FFF',
      borderRadius: 20,
      padding: '16px',
      marginBottom: 16,
    },
    table: {
      '& .MuiTableCell-root': {
        borderBottom: 'none',
        padding: '4px 8px',
        '&:first-child': {
          paddingLeft: 0,
        },
        '&:last-child': {
          paddingRight: 0,
        },
      },
      '& .MuiTableRow-root:nth-child(even)': {
        backgroundColor: '#f4f4f4',
      },
    },
    tableHeader: {
      fontFamily: 'SuraSans-Negrita',
      fontSize: '14px',
      color: '#0133A0',
      fontWeight: 'bold',
      marginBlock: 16,
      textAlign: 'center',
    },
    tableCell: {
      fontFamily: 'SuraSans-Regular',
      fontSize: '14px',
      color: '#0133A0',
      textAlign: 'center',
      padding: '14px 8px !important',
    },
    infoBox: {
      backgroundColor: '#E0EAFF',
      borderRadius: 20,
      padding: '16px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    infoIcon: {
      width: '35px',
      height: '35px',
      marginTop: '2px',
      '& path': {
        fill: '#0133A0',
      },
    },
    infoText: {
      fontFamily: 'SuraSans-Ligera',
      fontSize: '14px',
      color: '#0133A0',
      lineHeight: '20px',
      flex: 1,
    },
    actions: {
      padding: 0,
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 24,
    },
    cancelButton: {
      fontFamily: 'SuraSans-Regular',
      fontSize: '14px',
      color: '#0133A0',
      textTransform: 'none',
      padding: '8px 24px',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    acceptButton: {
      fontFamily: 'SuraSans-Regular',
      fontSize: '14px',
      color: '#0133A0',
      textTransform: 'none',
      padding: '8px 24px',
      borderRadius: 20,
      border: '1px solid #0133A0',
      '&:hover': {
        backgroundColor: 'transparent',
        border: '1px solid #0133A0',
      },
    },
  }),
);

const formatDate = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const ConfirmationModal: React.FC<IConfirmationModalProps> = observer(
  ({ open, onClose, onAccept }) => {
    const css = useStyles();
    const { links } = useStore();
    const contracts = links.paymentLink.sura.contracts;

    return (
      <Dialog open={open} onClose={onClose} className={css.dialog}>
        <DialogTitle disableTypography className={css.dialogTitle}>
          <Typography className={css.title}>Domiciliación de Pólizas</Typography>
          <Typography className={css.subtitle}>
            ¿Estás seguro de que deseas Domiciliar las siguientes pólizas?
          </Typography>
        </DialogTitle>
        <DialogContent className={css.content}>
          <div className={css.policyContainer}>
            <Table className={css.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={css.tableHeader}>Llave de póliza</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map((contract) => (
                  <TableRow key={contract.policyId}>
                    <TableCell className={css.tableCell}>{contract.policyId}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className={css.infoBox}>
            <ExclamationIcon className={css.infoIcon} />
            <Typography className={css.infoText}>
              A todas las pólizas seleccionadas se le domiciliarán pagos, cobros subsecuentes y
              renovaciones, con la misma forma de pago.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions className={css.actions}>
          <Button onClick={onClose} className={css.cancelButton}>
            Cancelar
          </Button>
          <Button onClick={onAccept} className={css.acceptButton}>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default ConfirmationModal;
